import React, { useState, useEffect } from 'react';
import { TextField, IconButton } from '@mui/material';
import { Edit } from 'react-feather';

type EditableTitleInlineProps = {
  title: string;
  onSave: (newTitle: string) => Promise<void>;
  maxLength?: number;
};

export const EditableTitleInline: React.FC<EditableTitleInlineProps> = ({
  title,
  onSave,
  maxLength,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state with prop when title changes from refetch
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  const handleBlur = async () => {
    if (localTitle.trim() === '' || localTitle === title) {
      setLocalTitle(title); // revert if empty or unchanged
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(localTitle);
      setIsEditing(false);
    } catch (error) {
      setLocalTitle(title); // revert on error
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleBlur();
    }
    if (e.key === 'Escape') {
      setLocalTitle(title);
      setIsEditing(false);
    }
  };

  if (!isEditing) {
    return (
      <>
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        <IconButton
          onClick={() => setIsEditing(true)}
          size="small"
          aria-label="Edit document title"
        >
          <Edit size={16} />
        </IconButton>
      </>
    );
  }

  return (
    <TextField
      value={localTitle}
      onChange={(e) => setLocalTitle(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={isSaving}
      autoFocus
      size="small"
      sx={{ minWidth: 400, maxWidth: 600 }}
      inputProps={{
        ...(maxLength && { maxLength }),
        'aria-label': 'Document title',
      }}
    />
  );
};
